import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Building2, ChevronLeft, Handshake, PieChart, Users as UsersIcon } from 'lucide-react';
import { useWorkspaces } from '../api/queries.ts';
import { useOrgCtx } from '../app/org-context.tsx';
import { PageLoader, EmptyState, WorkspaceAvatar } from '../ui/primitives.tsx';
import { useToast } from '../ui/Toast.tsx';
import { CrmView, type CrmSection } from '../features/crm/CrmView.tsx';

const GOOGLE_RESULT_MESSAGES: Record<string, string> = {
  connected: 'Email collegata al CRM di questo spazio',
  denied: 'Collegamento annullato: nessun permesso concesso',
  expired: 'Il collegamento è scaduto: riprova',
  error: 'Collegamento non riuscito: riprova',
};

export const CRM_SECTIONS: { id: CrmSection; label: string; icon: typeof Handshake }[] = [
  { id: 'pipeline', label: 'Pipeline', icon: Handshake },
  { id: 'companies', label: 'Aziende', icon: Building2 },
  { id: 'contacts', label: 'Contatti', icon: UsersIcon },
  { id: 'report', label: 'Report', icon: PieChart },
];

/** The CRM opens as its own full-screen "app" rather than a tab inside the workspace: own route
 * (`/w/:wsKey/crm[/section]`), own header, own navigation - on mobile the bottom tab bar itself swaps to
 * these CRM sections (see TabBar in features/shell/Chrome.tsx) instead of the app's usual 5 tabs. */
export function CrmAppPage({ section }: { section: CrmSection }) {
  const { wsKey } = useParams();
  const { base, orgId } = useOrgCtx();
  const workspaces = useWorkspaces(orgId);
  const ws = workspaces.data?.find((w) => w.key === wsKey);
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const r = params.get('google');
    if (r) {
      toast(GOOGLE_RESULT_MESSAGES[r] ?? 'Operazione completata');
      setParams((p) => { const n = new URLSearchParams(p); n.delete('google'); return n; }, { replace: true });
    }
  }, [params, setParams, toast]);

  if (workspaces.isLoading) return <PageLoader />;
  if (!ws) {
    return (
      <div className="page">
        <div className="card"><EmptyState tone="neutral" title="Spazio non trovato">Non esiste o non ne fai parte.</EmptyState></div>
      </div>
    );
  }

  return (
    <div className="page wide crm-app">
      <div className="crm-appbar">
        {/* On mobile the bottom tab bar already has its own exit (see TabBar's CRM mode in Chrome.tsx);
            on desktop that bar doesn't exist at all, so this stays the only way out there. */}
        <Link to={`${base}/w/${ws.key}`} className="icon-btn desktop-only" aria-label="Torna allo spazio"><ChevronLeft size={22} /></Link>
        <WorkspaceAvatar name={ws.name} keyLabel={ws.key} color={ws.color} logoUrl={ws.logoUrl} size={34} />
        <div className="crm-appbar-title">
          <span className="crm-appbar-eyebrow">CRM</span>
          <strong>{ws.name}</strong>
        </div>
        <nav className="crm-appbar-nav desktop-only" aria-label="Sezione CRM">
          {CRM_SECTIONS.map((s) => (
            <Link key={s.id} to={s.id === 'pipeline' ? `${base}/w/${ws.key}/crm` : `${base}/w/${ws.key}/crm/${s.id}`}
              className="chip" aria-pressed={section === s.id}>
              <s.icon size={15} /> {s.label}
            </Link>
          ))}
        </nav>
      </div>
      <div className="crm-app-body">
        <CrmView workspace={ws} section={section} />
      </div>
    </div>
  );
}
